import { useRef, useState } from 'react'

import { SystemSpaceSlug } from '@bettermode/common/space'
import { T } from '@tribeplatform/react-components/i18n'
import {
  NOTIFICATION_DROPDOWN_QUERY,
  NotificationDropdownList,
  NotificationsMarkAsRead,
  NotificationSettingsLink,
} from '@tribeplatform/react-components/Notification'
import { useRouter } from '@tribeplatform/react-sdk'
import {
  useNotifications,
  useNotificationsCount,
  useSpaceUtil,
} from '@tribeplatform/react-sdk/hooks'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import {
  IndicatorBadge,
  IndicatorBadgeWrapper,
} from '@tribeplatform/react-ui-kit/IndicatorBadge'
import { Popover } from '@tribeplatform/react-ui-kit/Popover'
import { TopBarButton } from '@tribeplatform/react-ui-kit/TopBar'

import { useOpenWhenReady } from '../hooks/useOpenWhenReady.js'

const NOTIFICATION_COUNT_REFETCH_INTERVAL = 12000 // ms

interface NotificationsButtonProps {
  label: string
}
export const NotificationsButton = ({ label }: NotificationsButtonProps) => {
  const { Link } = useRouter()
  const { generatePath } = useSpaceUtil()
  const [warmedUp, setWarmedUp] = useState(false)

  const { data: notificationCount = 0 } = useNotificationsCount({
    useQueryOptions: {
      refetchInterval: NOTIFICATION_COUNT_REFETCH_INTERVAL,
    },
  })

  // Fetched once the button is hovered or focused, so the notifications are
  // usually cached by the time it is clicked. Background refetches are left to
  // the dropdown's own observer, so this one does not keep refetching on every
  // page.
  const { isSuccess: notificationsReady } = useNotifications({
    ...NOTIFICATION_DROPDOWN_QUERY,
    useInfiniteQueryOptions: {
      enabled: warmedUp,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  })

  const activatorRef = useRef<HTMLDivElement>(null)
  const { open, toggle, close } = useOpenWhenReady(
    notificationsReady,
    activatorRef,
  )

  const warmUp = () => setWarmedUp(true)

  return (
    <div
      ref={activatorRef}
      className="h-full flex justify-center items-center shrink-0"
    >
      <Popover
        placement="bottom-end"
        open={open}
        onClose={close}
        activator={
          <IndicatorBadgeWrapper
            indicatorBadge={
              <IndicatorBadge count={notificationCount} size="sm" />
            }
          >
            <TopBarButton
              onClick={() => {
                warmUp()
                toggle()
              }}
              onMouseEnter={warmUp}
              onFocus={warmUp}
              icon={<SvgIcon name="bell" />}
              aria-label={label}
            />
          </IndicatorBadgeWrapper>
        }
        fluidContent={false}
        fixed
      >
        <div className="flex flex-col">
          <div className="border-b border-line-subdued font-semibold px-3 py-2 flex items-center gap-1">
            <Link
              className="flex-grow text-content-subdued hover:text-content-hovered"
              href={generatePath(SystemSpaceSlug.NOTIFICATIONS)}
              onClick={close}
            >
              <T defaultMessage="Notifications" id="Generics.Notifications" />
            </Link>
            <NotificationsMarkAsRead />
            <NotificationSettingsLink onClick={close} />
          </div>
          <div className="grow">
            <NotificationDropdownList closeDropdown={close} />
          </div>
        </div>
      </Popover>
    </div>
  )
}
